import {withDevtools} from "@angular-architects/ngrx-toolkit";
import {patchState, signalStore, withMethods, withState} from "@ngrx/signals";
import {ApiService} from "../services/api.service";
import {inject} from "@angular/core";
import {catchError, map, Observable, of, shareReplay, take, tap} from "rxjs";
import {UserService} from "../services/user.service";

export interface DynamicTranslationsStore {
  translations: {language: string, message: string, translation: string}[];
}

const initialState: DynamicTranslationsStore = {
  translations: []
};

export const DynamicTranslationsStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('DynamicTranslationsStore'),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const user: UserService = inject(UserService);
    
    // Cache for ongoing HTTP requests to prevent duplicate requests
    const pendingRequests = new Map<string, Observable<string>>();
    
    return {
      translate(textToTranslate: string, creatorId?: number): Observable<string> {
        if(creatorId !== undefined && user.getUser()!.id !== undefined && creatorId === user.getUser()!.id) {
          return of(textToTranslate);
        }
        if(textToTranslate === undefined || textToTranslate === null || textToTranslate.trim() === '') {
          return of(textToTranslate);
        }
        const alreadyTranslated = store.translations()
          .find((translation) => translation.message === textToTranslate && translation.language === user.getUser()!.locale);
        if(alreadyTranslated) {
          return of(alreadyTranslated.translation);
        } else {
          // Create a unique key for this translation request
          const requestKey = `${user.getUser()?.locale ?? ''}:${textToTranslate}`;
          
          // Check if there's already a pending request for this translation
          const pendingRequest = pendingRequests.get(requestKey);
          if(pendingRequest) {
            return pendingRequest;
          }
          
          // Create new request and cache it
          const request$ = api.translate(textToTranslate ?? '', creatorId ?? undefined)
            .pipe(
              take(1),
              map((translated: {message: string}) => {
                this.addTranslation(user.getUser()?.locale ?? '', textToTranslate ?? '', translated.message);
                return translated.message;
              }),
              catchError((error) => {
                console.error('Translation failed:', error);
                return of(textToTranslate);
              }),
              tap({
                finalize: () => {
                  // Remove from cache when request completes (success or error)
                  pendingRequests.delete(requestKey);
                }
              }),
              shareReplay(1)
            );
          
          // Store the request in the cache
          pendingRequests.set(requestKey, request$);
          
          return request$;
        }
      },
      addTranslation(language: string, message: string, translation: string): void {
        patchState(store, (state: DynamicTranslationsStore) => ({
          translations: [
            ...state.translations,
            {
              language,
              message,
              translation
            }
          ]
        }));
      },
    };
  })
);